import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { hubClient } from '../../services/hubClient';
import { errorText } from '../../lib/assistantView';
import type { BotSystem } from '../../lib/botNav';
import {
  SIGNALS,
  SIGNAL_LABEL,
  signalClass,
  summary,
  docLink,
  docHint,
  memberCountLabel,
  latestGate,
  type Metrics,
  type ReviewFilters,
  type ReviewGroup,
  type ReviewStatus,
} from '../../lib/answerReviewView';

const DAY_OPTIONS = [1, 7, 30] as const;
const STATUS_OPTIONS: { key: ReviewFilters['status']; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'fixed', label: 'Fixed' },
  { key: 'ignored', label: 'Ignored' },
  { key: 'all', label: 'All' },
];

/**
 * Quality › Answers to review (answer-quality spec, Phase 2a): how the bot's AI answers did in the window, and the weak
 * ones grouped by similar question — what the customer asked, what the bot said, the documents it used — so an operator
 * fixes the knowledge and marks the group fixed.
 */
export function AnswersReviewPanel({ system, botId }: { system: BotSystem; botId: string }) {
  const [filters, setFilters] = useState<ReviewFilters>({ days: 7, status: 'open', signal: '' });
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [groups, setGroups] = useState<ReviewGroup[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const metricsGate = useRef(latestGate());
  const groupsGate = useRef(latestGate());

  // The rates and the list load separately: grouping waits on the embedding service, the rates never should. Each
  // keeps only its newest reply, so quick filter changes never show an older window's data.
  const loadMetrics = useCallback(async () => {
    const id = metricsGate.current.begin();
    setMetrics(null);
    try {
      const m = await hubClient.answerReviewMetrics({ system, botId }, filters.days);
      if (metricsGate.current.isCurrent(id)) setMetrics(m);
    } catch (e) {
      if (metricsGate.current.isCurrent(id)) setError(errorText(e));
    }
  }, [system, botId, filters.days]);

  const load = useCallback(async () => {
    const id = groupsGate.current.begin();
    setError(null);
    setGroups(null);
    setTruncated(false);
    try {
      const r = await hubClient.answerReviewGroups({ system, botId }, filters);
      if (!groupsGate.current.isCurrent(id)) return;
      setGroups(r.groups);
      setTruncated(r.truncated);
    } catch (e) {
      if (!groupsGate.current.isCurrent(id)) return;
      setGroups([]);
      setError(errorText(e));
    }
  }, [system, botId, filters]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(g: ReviewGroup, status: ReviewStatus) {
    const asked = status === 'open' ? null : window.prompt(status === 'fixed' ? 'What did you change? (optional)' : 'Why ignore? (optional)');
    if (asked === null && status !== 'open') return; // cancelled
    setBusy(g.leaderTraceId);
    try {
      const bot = { system, botId };
      await hubClient.answerReviewDecide(bot, { traceIds: g.members.map((m) => m.traceId), status, note: asked || undefined });
      await Promise.all([load(), loadMetrics()]);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  const s = metrics ? summary(metrics.totals) : null;
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">How this bot's AI answers did, and the weak ones to fix — grouped by similar question.</p>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className="inline-flex overflow-hidden rounded border border-gray-300">
          {DAY_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setFilters((f) => ({ ...f, days: d }))}
              className={`px-3 py-1 ${filters.days === d ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {d === 1 ? 'Today' : `${d} days`}
            </button>
          ))}
        </div>
        <select
          className="border rounded px-2 py-1"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as ReviewFilters['status'] }))}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          className="border rounded px-2 py-1"
          value={filters.signal}
          onChange={(e) => setFilters((f) => ({ ...f, signal: e.target.value as ReviewFilters['signal'] }))}
        >
          <option value="">All signals</option>
          {SIGNALS.map((x) => (
            <option key={x} value={x}>
              {SIGNAL_LABEL[x]}
            </option>
          ))}
        </select>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
          {(
            [
              ['AI answers', String(s.total)],
              ['No data', s.noData],
              ['Fallback', s.fallback],
              ['Error', s.error],
              ['Weak match', s.weak],
              ['No documents', s.noDocs],
              ['Fixed', String(s.fixed)],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="text-lg font-semibold">{value}</div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
      {truncated && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2">
          Showing the newest 1 000 answers to review. Pick a shorter window or one signal to see the rest.
        </div>
      )}
      {groups === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing to review in this window.</p>
      ) : (
        <ul className="space-y-2">
          {groups.map((g) => (
            <li key={g.leaderTraceId} className="bg-white border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="text-gray-500" onClick={() => setOpen(open === g.leaderTraceId ? null : g.leaderTraceId)}>
                  {open === g.leaderTraceId ? '▾' : '▸'}
                </button>
                <span className="font-medium">{g.question}</span>
                <span className="text-gray-500">· {memberCountLabel(g.size)}</span>
                {SIGNALS.filter((x) => g.signals[x]).map((x) => (
                  <span key={x} className={`text-xs px-1.5 py-0.5 rounded ${signalClass(x)}`}>
                    {SIGNAL_LABEL[x]} {g.signals[x]}
                  </span>
                ))}
                {g.status !== 'open' && <span className="text-xs text-gray-500">{g.status}</span>}
                <span className="ml-auto flex gap-3">
                  {g.status !== 'fixed' && (
                    <button type="button" disabled={busy === g.leaderTraceId} className="text-green-700 hover:underline" onClick={() => void decide(g, 'fixed')}>
                      Mark fixed
                    </button>
                  )}
                  {g.status !== 'ignored' && (
                    <button type="button" disabled={busy === g.leaderTraceId} className="text-gray-600 hover:underline" onClick={() => void decide(g, 'ignored')}>
                      Ignore
                    </button>
                  )}
                  {g.status !== 'open' && (
                    <button type="button" disabled={busy === g.leaderTraceId} className="text-blue-600 hover:underline" onClick={() => void decide(g, 'open')}>
                      Reopen
                    </button>
                  )}
                </span>
              </div>
              {open === g.leaderTraceId && (
                <div className="mt-3 space-y-3">
                  {g.documents.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Documents used</div>
                      <ul className="space-y-1">
                        {g.documents.map((d) => (
                          <li key={d.id}>
                            {d.exists ? (
                              <Link className="text-blue-600 hover:underline" to={docLink(system, botId, d.id)}>
                                {d.title || `Document ${d.id}`}
                              </Link>
                            ) : (
                              <span className="text-gray-400">Document {d.id} (no longer exists)</span>
                            )}
                            {docHint(d.origin) && <div className="text-xs text-amber-800">{docHint(d.origin)}</div>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <ul className="divide-y">
                    {g.members.map((m) => (
                      <li key={m.traceId} className="py-2">
                        <div className="text-xs text-gray-500">
                          {new Date(m.createdAt).toLocaleString()} · <span className={`px-1 rounded ${signalClass(m.signal)}`}>{SIGNAL_LABEL[m.signal]}</span>
                          {m.bestScore != null && ` · best match ${m.bestScore.toFixed(2)}`}
                          {m.status !== 'open' && ` · ${m.status}${m.note ? `: ${m.note}` : ''}`}
                        </div>
                        <div>
                          <span className="text-gray-500">Q:</span> {m.question}
                        </div>
                        <div className="whitespace-pre-wrap">
                          <span className="text-gray-500">A:</span> {m.answer ?? '—'}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
